import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

// Constants
import {
  ACCOUNT_TYPE_OPTIONS,
  ACCOUNT_STATUS_OPTIONS,
  NATIONAL_ID_TYPE_OPTIONS,
  BOOLEAN_OPTIONS,
  // ACTIVITY_INDIVIDUAL_OPTIONS
} from 'common/constants/formOptions'
import { COUNTRIES_LIST } from 'common/constants/countries'

// Store
import { actions } from 'core/store'

// Styled Elements
import {
  FormWrapper,
  FormInputGroupItem,
  FormGroupWrapper,
  TableButtonWrapper,
  TableRowWrapper,
  TablePanelWrapper,
} from './AccountIndividualDetailsForm.elements'

// Views
import {
  Form,
  FormTextField,
  FormSelect,
  FormSearchSelect,
  FormDatePicker,
  FormTextArea,
  AccordionTableItem,
} from 'views/components'

// Map Redux Props
const mapStateToProps = (state) => state
const mapDispatchToProps = actions

function AccountIndividualDetailsForm(props) {
  // Destructure
  const { values, viewMode, formRef, onSubmit } = props

  // Internal State
  const [initialData, setInitialData] = useState()
  // Forms
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(true)
  const [isAddressExpanded, setIsAddressExpanded] = useState(false)
  const [isFreeFormAddressExpanded, setIsFreeFormAddressExpanded] = useState(false)
  const [isNationalIDExpanded, setIsNationalIDExpanded] = useState(false)

  // Variables
  const validationSchema = Yup.object().shape({
    reference_id: Yup.string().max(50).required('Reference ID is required'),
    customer_id: Yup.string()
      .max(50)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context)
      ),
    email: Yup.string().email().nullable(),
    name_first: Yup.string().max(50).nullable(),
    name_middle: Yup.string().max(50).nullable(),
    name_last: Yup.string().max(50).required('Last Name is required'),
    is_customer: Yup.bool().required('Specify if account is a customer'),
    date_of_birth: Yup.date()
      .max(new Date(Date.now() - 567648000000), 'Must be atleast 18 years')
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context)
      )
      .test(
        'date_of_birth_with_place',
        'Date of birth is required when place of birth is provided',
        (value, context) => {
          const placeOfBirth = context.parent.place_of_birth
          if (placeOfBirth && !value) {
            return false // Fail if place is provided but date isn't
          }
          return true
        }
      ),
    place_of_birth: Yup.string()
      .max(50)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context)
      )
      .test(
        'place_of_birth_with_date',
        'Place of birth is required when date of birth is provided',
        (value, context) => {
          const dateOfBirth = context.parent.date_of_birth
          if (dateOfBirth && !value) {
            return false // Fail if date is provided but place isn't
          }
          return true
        }
      ),
    nationality_country_code: Yup.string().max(2).nullable(),
    address_line: Yup.string()
      .max(256)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context)
      )
      .test(
        'address_line',
        'Required if street number, street name, and building name are not specified',
        (value, context) => hasRequiredAddressFields(context)
      ),
    address_street_no: Yup.string()
      .max(10)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context)
      )
      .test('address_street_no', 'Required with street name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context)
      ),
    address_street_name: Yup.string()
      .max(100)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context)
      )
      .test('address_street_name', 'Required with building name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context)
      ),
    address_building_name: Yup.string()
      .max(20)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context)
      )
      .test('address_building_name', 'Required with street name if address line is not specified', (value, context) =>
        hasRequiredAddressFields(context)
      ),
    address_floor: Yup.string().max(20).nullable(),
    address_room: Yup.string().max(20).nullable(),
    address_department: Yup.string().max(20).nullable(),
    address_postbox: Yup.string().max(20).nullable(),
    address_city_location: Yup.string().max(50).nullable(),
    address_city: Yup.string()
      .max(50)
      .nullable()
      .test('address_city', 'Required if address is provided', (value, context) => hasAddress(value, context)),
    address_region: Yup.string().max(50).nullable(),
    address_district: Yup.string().max(50).nullable(),
    address_postcode: Yup.string().max(25).nullable(),
    address_country_code: Yup.string()
      .max(2)
      .nullable()
      .test('address_country_code', 'Required if address is provided', (value, context) => hasAddress(value, context)),
    national_id_type_code: Yup.string()
      .max(10)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context)
      )
      .test(
        'national_id_type_required',
        'ID Type is required when National ID or Country Issuer is provided',
        (value, context) => {
          const hasNationalId = Boolean(context.parent.national_id)
          const hasIssueCountry = Boolean(context.parent.national_id_issue_country)

          // If any other National ID fields are provided, this field is required
          if ((hasNationalId || hasIssueCountry) && !value) {
            return false
          }
          return true
        }
      ),
    national_id: Yup.string()
      .max(35)
      .nullable()
      .test(
        'required-identifiers',
        'Customer ID, Date & Place of Birth, National ID, or Address is required',
        (value, context) => validateRequiredAccountFields(context)
      )
      .test(
        'national_id_required',
        'National ID is required when ID Type or Country Issuer is provided',
        (value, context) => {
          const hasIdType = Boolean(context.parent.national_id_type_code)
          const hasIssueCountry = Boolean(context.parent.national_id_issue_country)

          // If any other National ID fields are provided, this field is required
          if ((hasIdType || hasIssueCountry) && !value) {
            return false
          }
          return true
        }
      ),
    national_id_issue_country: Yup.string().max(2).nullable(),
  })

  // Functions
  function hasRequiredAddressFields(context) {
    const parent = context?.parent

    /*
      If the form already has required account fields
      (date and place of birth, customer id, or national id),
      skip required address fields validation
    */
    const hasRequiredAccountFields = validateRequiredAccountFields(context)
    if (hasRequiredAccountFields) {
      return true
    }

    // Check if any address fields are filled
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasBuildingName = Boolean(parent?.address_building_name)
    const hasStreetName = Boolean(parent?.address_street_name)
    const hasAddress = hasAddressLine || (hasStreetName && (hasStreetNo || hasBuildingName))

    return hasAddress
  }

  function hasAddress(value, context) {
    const parent = context?.parent

    // Check if any address fields are filled
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasBuildingName = Boolean(parent?.address_building_name)
    const hasStreetName = Boolean(parent?.address_street_name)

    // If no address info is provided, value is optional
    if (!hasAddressLine && !hasStreetNo && !hasStreetName && !hasBuildingName) {
      return true
    }

    // If we have validated address information, value is required
    return Boolean(value)
  }

  /*
    Validates if at least one of:
    - Date and Place of Birth
    - National ID
    - Address
    - Customer ID
    is provided.

    If an attempt to provide one of these identifiers is made,
    even if the identifier is incomplete
    (e.g. specifying incomplete address, national ID, or date and place of birth),
    the validation will pass.
  */
  function validateRequiredAccountFields(context) {
    const parent = context?.parent

    // Check if at least date or place of birth is provided
    // The validation here is more relaxed, since we also require the other field if one is provided
    const hasDateOfBirth = Boolean(parent?.date_of_birth)
    const hasPlaceOfBirth = Boolean(parent?.place_of_birth)
    const hasBirthInfo = hasDateOfBirth || hasPlaceOfBirth

    // Check if any national ID fields are provided
    // The validation here is more relaxed, since we also require the other field if one is provided
    const hasNationalIdType = Boolean(parent?.national_id_type_code)
    const hasNationalID = Boolean(parent?.national_id)
    const hasNationalIDIssueCountry = Boolean(parent?.national_id_issue_country)

    const hasNationalIDInfo = hasNationalIdType || hasNationalID || hasNationalIDIssueCountry

    // Check if customer ID is provided
    const hasCustomerID = Boolean(parent?.customer_id)

    // Check if any required address is provided
    const hasAddressLine = Boolean(parent?.address_line)
    const hasStreetNo = Boolean(parent?.address_street_no)
    const hasStreetName = Boolean(parent?.address_street_name)
    const hasBuildingName = Boolean(parent?.address_building_name)

    const hasAddress = hasAddressLine || hasStreetName || hasStreetNo || hasBuildingName

    // Return true if at least one identifier method is provided
    return hasBirthInfo || hasNationalIDInfo || hasAddress || hasCustomerID
  }

  // UseEffects
  useEffect(() => {
    // TODO REMOVE HARD CODED VALUE
    setInitialData({ ...values, address_type_code: 'GEOG' })
  }, [values])
  return (
    <FormWrapper>
      {initialData && (
        <Form formRef={formRef} initialValues={initialData} validationSchema={validationSchema} onSubmit={onSubmit}>
          <AccordionTableItem
            style={{ marginTop: '20px' }}
            setIsExpanded={(val) => {
              setIsDetailsExpanded(val)
            }}
            expandPanel={isDetailsExpanded}
            button={
              <TableButtonWrapper>
                <TableRowWrapper isExpanded={isDetailsExpanded}>Personal information</TableRowWrapper>
              </TableButtonWrapper>
            }
            panel={
              <TablePanelWrapper>
                <FormGroupWrapper>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Reference ID*" name="reference_id" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSelect disabled label="Account Type" name="account_type_id" options={ACCOUNT_TYPE_OPTIONS} />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Customer ID*" name="customer_id" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSelect disabled={viewMode} label="Is Customer*" name="is_customer" options={BOOLEAN_OPTIONS} />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSelect disabled={viewMode} label="Status" name="status_id" options={ACCOUNT_STATUS_OPTIONS} />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Email" type="email" name="email" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSearchSelect
                      disabled={viewMode}
                      label="Nationality"
                      name="nationality_country_code"
                      options={COUNTRIES_LIST}
                    />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="First Name" name="name_first" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Middle Name" name="name_middle" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Last Name*" name="name_last" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormDatePicker disabled={viewMode} label="Date of Birth" name="date_of_birth" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Place of Birth" name="place_of_birth" />
                  </FormInputGroupItem>

                  <FormInputGroupItem>
                    <FormDatePicker disabled label="Created" name="created" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormDatePicker disabled label="Last Modified" name="last_modified" />
                  </FormInputGroupItem>
                </FormGroupWrapper>
              </TablePanelWrapper>
            }
          />
          <AccordionTableItem
            style={{ marginTop: '20px' }}
            setIsExpanded={(val) => {
              setIsAddressExpanded(val)
            }}
            expandPanel={isAddressExpanded}
            button={
              <TableButtonWrapper>
                <TableRowWrapper isExpanded={isAddressExpanded}>Structured address</TableRowWrapper>
              </TableButtonWrapper>
            }
            panel={
              <TablePanelWrapper>
                <FormGroupWrapper>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Street Number" name="address_street_no" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Street Name" name="address_street_name" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Building Name" name="address_building_name" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Floor" name="address_floor" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Room" name="address_room" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Department" name="address_department" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Postbox" name="address_postbox" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="City Location" name="address_city_location" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="City" name="address_city" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Region" name="address_region" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="District" name="address_district" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="Zipcode" name="address_postcode" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSearchSelect
                      disabled={viewMode}
                      label="Country"
                      name="address_country_code"
                      options={COUNTRIES_LIST}
                    />
                  </FormInputGroupItem>
                </FormGroupWrapper>
              </TablePanelWrapper>
            }
          />

          <AccordionTableItem
            style={{ marginTop: '20px' }}
            setIsExpanded={(val) => {
              setIsFreeFormAddressExpanded(val)
            }}
            expandPanel={isFreeFormAddressExpanded}
            button={
              <TableButtonWrapper>
                <TableRowWrapper isExpanded={isFreeFormAddressExpanded}>Free Form address</TableRowWrapper>
              </TableButtonWrapper>
            }
            panel={
              <TablePanelWrapper>
                <FormGroupWrapper>
                  <FormInputGroupItem>
                    <FormTextArea disabled={viewMode} label="Address Line" name="address_line" />
                  </FormInputGroupItem>
                </FormGroupWrapper>
              </TablePanelWrapper>
            }
          />

          <AccordionTableItem
            style={{ marginTop: '20px' }}
            setIsExpanded={(val) => {
              setIsNationalIDExpanded(val)
            }}
            expandPanel={isNationalIDExpanded}
            button={
              <TableButtonWrapper>
                <TableRowWrapper isExpanded={isNationalIDExpanded}>National ID</TableRowWrapper>
              </TableButtonWrapper>
            }
            panel={
              <TablePanelWrapper>
                <FormGroupWrapper>
                  <FormInputGroupItem>
                    <FormSelect
                      disabled={viewMode}
                      label="National ID Type"
                      name="national_id_type_code"
                      options={NATIONAL_ID_TYPE_OPTIONS}
                    />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField disabled={viewMode} label="National ID" name="national_id" />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormSelect
                      disabled={viewMode}
                      label="National ID Country Issuer"
                      name="national_id_issue_country"
                      options={COUNTRIES_LIST}
                    />
                  </FormInputGroupItem>
                  <FormInputGroupItem>
                    <FormTextField
                      disabled={viewMode}
                      label="National ID Registration Authority"
                      name="national_id_registration_authority"
                    />
                  </FormInputGroupItem>
                </FormGroupWrapper>
              </TablePanelWrapper>
            }
          />
        </Form>
      )}
    </FormWrapper>
  )
}

// Default Props
AccountIndividualDetailsForm.defaultProps = {
  formRef: {},
  form: {},
  actions: {},
  values: {
    reference_id: '',
    account_type_id: 0,
    status_id: 0,
    email: '',
    primary_activity_code: 0,
    nationality_country_code: '',
    name_first: '',
    name_middle: '',
    name_last: '',
    date_of_birth: '',

    created: '',
    last_modified: '',

    address_street_name: '',
    address_city: '',
    address_region: '',
    address_postcode: '',
    address_country_code: '',
  },
  viewMode: true,
}

// Proptypes Validation
AccountIndividualDetailsForm.propTypes = {
  formRef: PropTypes.shape({ root: PropTypes.string }),
  form: PropTypes.shape({
    COUNTRIES_LIST: PropTypes.instanceOf(Array),
    toBeUpdatedAccount: PropTypes.shape({}),
  }),
  actions: PropTypes.shape({
    setCountryListOptions: PropTypes.func,
    setIsEntityAccountDrawerOpen: PropTypes.func,
    setToBeUpdatedAccount: PropTypes.func,
    setIsPageTableUpdated: PropTypes.func,
    showAlert: PropTypes.func,
  }),
  values: PropTypes.shape({
    reference_id: PropTypes.string,
    account_type_id: PropTypes.number,
    status_id: PropTypes.number,
    email: PropTypes.string,
    primary_activity_code: PropTypes.number,
    nationality_country_code: PropTypes.number,
    name_first: PropTypes.string,
    name_middle: PropTypes.string,
    name_last: PropTypes.string,
    date_of_birth: PropTypes.string,

    created: PropTypes.string,
    last_modified: PropTypes.string,

    address_street_name: PropTypes.string,
    address_city: PropTypes.string,
    address_region: PropTypes.string,
    address_postcode: PropTypes.string,
    address_country_code: PropTypes.string,
  }),
  viewMode: PropTypes.bool,
  onSubmit: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(AccountIndividualDetailsForm)
